import type { UserRole } from '@/api/models';
import { PATH } from '@/router/PATH';
import type { AuthMethod } from '@/utils/authSession';

/** Tab identity — also the `Tabs` selection key and the testid discriminator. */
export type BranchesTabId = 'branches' | 'shifts';

export type BranchesTab = {
    id: BranchesTabId;
    /** Absolute route this tab owns; selecting the tab navigates here. */
    path: string;
    labelKey: string;
    /**
     * Roles that may see this tab at all. Absent means every caller who reached
     * the Branches shell — which, unlike the `/accounting` shells, is *every*
     * signed-in role: the branch list is on no role gate at all.
     *
     * That is exactly why the gate has to live here rather than on the sidebar
     * item, where it used to. `/sales/shifts` had its own nav row and carried
     * `roles` on it; folded into an area a Seller can open, the row is gone and
     * the rule would have gone with it. See `shiftsAccess` for the API side.
     */
    roles?: UserRole[];
};

/**
 * The Branches tabs, in display order. The first is the default: the bare
 * `/branches` renders it through an index route, and — unlike the accounting
 * shells — that is its **only** path. See `PATH.BRANCHES`.
 */
export const BRANCHES_TABS: BranchesTab[] = [
    {
        id: 'branches',
        path: PATH.BRANCHES.ROOT,
        labelKey: 'branches.tabs.branches',
    },
    {
        id: 'shifts',
        path: PATH.BRANCHES.SHIFTS,
        labelKey: 'branches.tabs.shifts',
        // A back-office audit surface: bos-api gates the shift register on
        // OrgAdmin | BranchAdmin with a password session, so a seller would
        // only ever reach a 403.
        roles: ['OrgAdmin', 'BranchAdmin'],
    },
];

export const DEFAULT_BRANCHES_TAB_ID: BranchesTabId = 'branches';

/**
 * Which tab the current URL selects.
 *
 * Matched **most specific first**, which the sibling shells get for free and
 * this one does not: the default tab's path is the shell root, so a prefix test
 * against it swallows every child route — `/branches/shifts` would select the
 * branch list. Longest path wins, and anything unrecognised falls back to the
 * default tab so the bar is never rendered with nothing selected.
 */
export const resolveBranchesTabId = (pathname: string): BranchesTabId => {
    const matched = [...BRANCHES_TABS]
        .sort((a, b) => b.path.length - a.path.length)
        .find(
            (tab) =>
                pathname === tab.path || pathname.startsWith(`${tab.path}/`),
        );

    return matched?.id ?? DEFAULT_BRANCHES_TAB_ID;
};

/** Route target for a tab id; unknown ids land on the default tab's route. */
export const resolveBranchesTabPath = (id: string): string => {
    const matched = BRANCHES_TABS.find((tab) => tab.id === id);

    return matched?.path ?? PATH.BRANCHES.ROOT;
};

/**
 * Whether a caller's session may see a tab.
 *
 * The role is only half of it: the API's admin policies also require
 * `auth_method=password`, so an OrgAdmin holding a PIN-issued kiosk token 403s
 * exactly as a Seller does. Failing closed on an unknown auth method is
 * deliberate — a tab that should have been hidden is a worse mistake than a tab
 * briefly missing.
 */
const isTabVisible = (
    tab: BranchesTab,
    role: UserRole | null,
    authMethod: AuthMethod | null,
): boolean => {
    if (!tab.roles) {
        return true;
    }
    return (
        authMethod === 'password' && role !== null && tab.roles.includes(role)
    );
};

/**
 * The tab bar for this session, and which tab the URL selects.
 *
 * The selected tab is **always** in the returned list, even when the caller's
 * role would hide it. That is not a loophole: each tab's panel is what renders
 * the routed `<Outlet />`, so dropping the selected tab would leave a seller who
 * followed a direct link to `/branches/shifts` with a blank screen instead of
 * the register's own explanation of why it is unavailable to them. The page
 * refuses; the bar only stops advertising. Navigate away and the tab is gone.
 */
export const branchesTabsFor = (
    pathname: string,
    role: UserRole | null,
    authMethod: AuthMethod | null,
): { tabs: BranchesTab[]; selectedTabId: BranchesTabId } => {
    const selectedTabId = resolveBranchesTabId(pathname);

    return {
        tabs: BRANCHES_TABS.filter(
            (tab) =>
                tab.id === selectedTabId || isTabVisible(tab, role, authMethod),
        ),
        selectedTabId,
    };
};
